"use client";

import {
  useCallback,
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

// Template Types
export type ProjectStage =
  "pre-construction" | "under-construction" | "operational" | "punch-list";
export type UserRole = "auditor" | "reviewer" | "admin";

export interface TemplateSection {
  id: string;
  name: string;
  description: string;
  component: string;
  readOnly: boolean;
  required: boolean;
  stage: ProjectStage[];
}

export interface ProjectTemplate {
  id: string;
  stage: ProjectStage;
  name: string;
  description: string;
  sections?: TemplateSection[];
}

export interface AuditSession {
  siteId: string;
  projectStage: ProjectStage;
  userRole: UserRole;
  userId: string;
  createdAt: number;
  lastModified: number;
  templateId: string;
}

interface SolarTemplateContextType {
  selectedTemplateName: ProjectStage | null;
  setSelectedTemplateName: React.Dispatch<
    React.SetStateAction<ProjectStage | null>
  >;
  selectedVersion: number;
  setSelectedVersion: React.Dispatch<React.SetStateAction<number>>;
  // Template Management
  currentTemplate: ProjectTemplate | null;
  availableTemplates: ProjectTemplate[];
  selectTemplate: (stage: ProjectStage) => void;
  getTemplateSections: (stage: ProjectStage) => TemplateSection[];
  createCustomTemplate: (template: ProjectTemplate) => void;
  deleteCustomTemplate: (templateId: string) => void;

  // Session Management
  currentSession: AuditSession | null;
  initializeSession: (
    siteId: string,
    stage: ProjectStage,
    role: UserRole,
    userId: string,
  ) => void;
  updateSessionStage: (newStage: ProjectStage) => void;
  clearSession: () => void;

  // Role-Based Access
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  canEditSection: (sectionId: string) => boolean;
  canViewSection: (sectionId: string) => boolean;
  getPermissions: () => Record<string, boolean>;

  // Storage Management
  saveTemplateProgress: (sectionId: string, data: any) => void;
  loadTemplateProgress: (sectionId: string) => any;
  clearTemplateProgress: () => void;
}

const SolarTemplateContext = createContext<
  SolarTemplateContextType | undefined
>(undefined);

const DEFAULT_TEMPLATES: ProjectTemplate[] = [
  {
    id: "pre-construction",
    stage: "pre-construction",
    name: "Pre-Construction Audit",
    description:
      "Planning and feasibility assessment before construction begins",
  },
  {
    id: "under-construction",
    stage: "under-construction",
    name: "Under-Construction Audit",
    description: "Monthly monitoring during active construction phase",
  },
  {
    id: "operational",
    stage: "operational",
    name: "Operational Project Audit",
    description: "Post-construction assessment and operational review",
  },
  {
    id: "punch-list",
    stage: "punch-list",
    name: "Punch List & Final Inspection",
    description: "Final defect list and project completion review",
  },
];

// ROLE-BASED PERMISSIONS
const ROLE_PERMISSIONS: Record<UserRole, Record<string, boolean>> = {
  auditor: {
    "project-data": true,
    "site-data": true,
    "data-on-lines-wires-pipes": true,
    "grid-connection-information": true,
    "planning-aspects": true,
    "construction-access": true,
    "shading-objects": true,
    "land-ownership": true,
    "civil-works": true,
    "mechanical-works": true,
    "electrical-works": true,
    "key-issue-matrix": true,
    "monthly-activities": true,
    "construction-progress": true,
    "lifting-activities": true,
    "hse-management": true,
    "overall-site-observations": true,
    "site-data-cdm": true,
    view_all_audits: false,
    manage_templates: false,
    manage_users: false,
  },
  reviewer: {
    "project-data": false,
    "site-data": false,
    "data-on-lines-wires-pipes": false,
    "grid-connection-information": false,
    "planning-aspects": false,
    "construction-access": false,
    "shading-objects": false,
    "land-ownership": false,
    "civil-works": false,
    "mechanical-works": false,
    "electrical-works": false,
    "key-issue-matrix": true,
    "monthly-activities": true,
    "construction-progress": true,
    "lifting-activities": true,
    "hse-management": true,
    "overall-site-observations": true,
    "site-data-cdm": false,
    view_all_audits: true,
    manage_templates: false,
    manage_users: false,
  },
  admin: {
    "project-data": true,
    "site-data": true,
    "data-on-lines-wires-pipes": true,
    "grid-connection-information": true,
    "planning-aspects": true,
    "construction-access": true,
    "shading-objects": true,
    "land-ownership": true,
    "civil-works": true,
    "mechanical-works": true,
    "electrical-works": true,
    "key-issue-matrix": true,
    "monthly-activities": true,
    "construction-progress": true,
    "lifting-activities": true,
    "hse-management": true,
    "overall-site-observations": true,
    "site-data-cdm": true,
    view_all_audits: true,
    manage_templates: true,
    manage_users: true,
  },
};

export function SolarTemplateProvider({ children }: { children: ReactNode }) {
  const [currentTemplate, setCurrentTemplate] =
    useState<ProjectTemplate | null>(null);
  const [currentSession, setCurrentSession] = useState<AuditSession | null>(
    null,
  );
  const [userRole, setUserRoleState] = useState<UserRole>("auditor");
  const [templates, setTemplates] =
    useState<ProjectTemplate[]>(DEFAULT_TEMPLATES);
  const [selectedTemplateName, setSelectedTemplateName] =
    useState<ProjectStage | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number>(0);

  useEffect(() => {
    const savedSession = window.sessionStorage?.getItem("auditSession");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setCurrentSession(session);
        const template = templates.find(
          (t) => t.stage === session.projectStage,
        );
        if (template) setCurrentTemplate(template);
      } catch (error) {
        console.error("Failed to load session:", error);
      }
    }

    const savedRole = localStorage.getItem("userRole");
    if (savedRole) {
      setUserRoleState(savedRole as UserRole);
    }
  }, [templates]);

  const selectTemplate = useCallback(
    (stage: ProjectStage) => {
      const template = templates.find((t) => t.stage === stage);
      if (template) {
        setCurrentTemplate(template);
        if (currentSession) {
          const updatedSession = {
            ...currentSession,
            projectStage: stage,
            lastModified: Date.now(),
          };
          setCurrentSession(updatedSession);
          window.sessionStorage.setItem(
            "auditSession",
            JSON.stringify(updatedSession),
          );
        }
      }
    },
    [templates, currentSession],
  );

  const getTemplateSections = (stage: ProjectStage): TemplateSection[] => {
    const template = templates.find((t) => t.stage === stage);
    return template?.sections || [];
  };

  const initializeSession = (
    siteId: string,
    stage: ProjectStage,
    role: UserRole,
    userId: string,
  ) => {
    const session: AuditSession = {
      siteId,
      projectStage: stage,
      userRole: role,
      userId,
      createdAt: Date.now(),
      lastModified: Date.now(),
      templateId: `${stage}-template`,
    };
    setCurrentSession(session);
    setUserRoleState(role);

    window.sessionStorage.setItem("auditSession", JSON.stringify(session));
    localStorage.setItem("userRole", role);
    localStorage.setItem("lastSiteId", siteId);

    // Load template
    const template = templates.find((t) => t.stage === stage);
    if (template) setCurrentTemplate(template);
  };

  const updateSessionStage = (newStage: ProjectStage) => {
    if (currentSession) {
      const updated = {
        ...currentSession,
        projectStage: newStage,
        lastModified: Date.now(),
      };
      setCurrentSession(updated);
      window.sessionStorage.setItem("auditSession", JSON.stringify(updated));
      selectTemplate(newStage);
    }
  };

  const clearSession = () => {
    setCurrentSession(null);
    setCurrentTemplate(null);
    window.sessionStorage?.removeItem("auditSession");
  };

  const setUserRole = (role: UserRole) => {
    setUserRoleState(role);
    localStorage.setItem("userRole", role);
  };

  const canEditSection = (sectionId: string): boolean => {
    const permissions = ROLE_PERMISSIONS[userRole];
    const hasPermission = permissions[sectionId] || false;
    const section = currentTemplate?.sections.find((s) => s.id === sectionId);
    return hasPermission && !section?.readOnly;
  };

  const canViewSection = (sectionId: string): boolean => {
    const permissions = ROLE_PERMISSIONS[userRole];
    return permissions[sectionId] || false;
  };

  const getPermissions = (): Record<string, boolean> => {
    return ROLE_PERMISSIONS[userRole];
  };

  const saveTemplateProgress = (sectionId: string, data: any) => {
    const key = `template_${currentSession?.siteId}_${sectionId}`;

    window.sessionStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(key, JSON.stringify(data));
  };

  const loadTemplateProgress = (sectionId: string): any => {
    const key = `template_${currentSession?.siteId}_${sectionId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  };

  const clearTemplateProgress = () => {
    if (currentSession) {
      const prefix = `template_${currentSession.siteId}_`;
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(prefix)) {
          window.sessionStorage.removeItem(key);
        }
      });
    }
  };

  const createCustomTemplate = (template: ProjectTemplate) => {
    setTemplates((prev) => [...prev, template]);
    localStorage.setItem(
      `custom_template_${template.id}`,
      JSON.stringify(template),
    );
  };

  const deleteCustomTemplate = (templateId: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    localStorage.removeItem(`custom_template_${templateId}`);
  };

  return (
    <SolarTemplateContext.Provider
      value={{
        currentTemplate,
        availableTemplates: templates,
        selectTemplate,
        selectedTemplateName,
        setSelectedTemplateName,
        selectedVersion,
        setSelectedVersion,
        getTemplateSections,
        currentSession,
        initializeSession,
        updateSessionStage,
        clearSession,
        userRole,
        setUserRole,
        canEditSection,
        canViewSection,
        getPermissions,
        saveTemplateProgress,
        loadTemplateProgress,
        clearTemplateProgress,
        createCustomTemplate,
        deleteCustomTemplate,
      }}
    >
      {children}
    </SolarTemplateContext.Provider>
  );
}

export function useSolarTemplate() {
  const context = useContext(SolarTemplateContext);
  if (context === undefined) {
    throw new Error(
      "useSolarTemplate must be used within SolarTemplateProvider",
    );
  }
  return context;
}
